// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Types and schemas for customer-related data in the Hardfin application.
 * This module uses Zod for runtime type validation and TypeScript type inference.
 */
import {
  sparseCustomerSchema,
  sparseLocationWithParentSchema,
} from '@/sparse/api/types';
import { integrationListSchema } from '@/utilities/integration';
import { z } from 'zod';

/**
 * Complete customer schema extending the sparse schema with additional details.
 * Includes asset counts, location information, billing details, and logo information.
 */
export const customerSchema = sparseCustomerSchema.extend({
  assetCount: z.number(), // Total number of assets associated with the customer
  locationCount: z.number(), // Total number of locations for this customer
  assetsAtLocation: z.number(), // Number of assets currently deployed at customer locations
  upcomingProjectCt: z.number(), // Number of upcoming projects
  activeProjectCt: z.number(), // Number of active projects
  pastProjectCt: z.number(), // Number of past projects
  incompleteProjectCt: z.number(), // Number of incomplete date range projects
  locations: z
    .array(sparseLocationWithParentSchema)
    .nullable()
    .transform((v) => v ?? []),
  logoFileKey: z.string().nullable(), // Key for customer's logo in file storage
  domain: z.string().nullable(), // Customer's website domain
  billingContact: z
    .object({
      name: z.string().nullable(),
      email: z.string().nullable(),
      phone: z.string().nullable(),
    })
    .nullable(),
  billingAddress: z.string().nullable(),
  comment: z.string().nullable(),
  externalId: z.string().nullable(),
  integrations: integrationListSchema,
});

export type Customer = z.infer<typeof customerSchema>;
